import React, { useState } from "react";
import { Alert, Button, Card, Input, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";

const SESSION_KEY = "rfm_admin_access_granted";

interface AdminAccessGateProps {
  children: React.ReactNode;
}

// Client-side passcode gate. This only deters casual/accidental access to
// admin pages embedded in the app's own UI — the passcode ships inside the
// public JS bundle (as with any REACT_APP_* env var) and the underlying API
// endpoints are not protected by it. Real access control must be enforced
// server-side.
const AdminAccessGate: React.FC<AdminAccessGateProps> = ({ children }) => {
  const configuredPasscode = process.env.REACT_APP_ADMIN_PASSCODE;
  const [granted, setGranted] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "true",
  );
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (granted) return <>{children}</>;

  const handleSubmit = () => {
    if (!configuredPasscode) {
      setError(
        "Admin access is not configured. Contact the administrator.",
      );
      return;
    }
    if (value === configuredPasscode) {
      sessionStorage.setItem(SESSION_KEY, "true");
      setGranted(true);
      setError(null);
    } else {
      setError("Incorrect passcode.");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backgroundColor: "#F5F7FA",
      }}
    >
      <Card style={{ width: 360 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <LockOutlined style={{ fontSize: 28, color: "#1565C0" }} />
          <Typography.Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
            Admin Access Required
          </Typography.Title>
          <Typography.Text type="secondary">
            Enter the admin passcode to continue.
          </Typography.Text>
        </div>
        <Input.Password
          placeholder="Passcode"
          value={value}
          autoFocus
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onPressEnter={handleSubmit}
          status={error ? "error" : undefined}
        />
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginTop: 12 }}
          />
        )}
        <Button type="primary" block style={{ marginTop: 16 }} onClick={handleSubmit}>
          Continue
        </Button>
      </Card>
    </div>
  );
};

export default AdminAccessGate;
